import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { QrBarcodeScanner } from "../../../../components/common/QrBarcodeScanner/QrBarcodeScanner";
import { beep } from "../../../../utils/utils";
import { logError } from "../../../../utils/appUtils";
import { useNotify } from "react-admin";

export const SealsCreate = ({ cancel, saveSeal, saving, addedSeals }) => {
  const [isScannerOpen, setIsScannerOpen] = useState(true);
  const [sealBarCode, setSealBarCode] = useState("");
  const [notes, setNotes] = useState("");
  const [sealError, setSealError] = useState("");
  const [isScanned, setIsScanned] = useState(false);
  const [alreadyAddedDialogOpen, setAlreadyAddedDialogOpen] = useState(false);
  const [notInSealsNumberDialogOpen, setNotInSealsNumberDialogOpen] =
    useState(false);
  const notify = useNotify();
  const ref = useRef();
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      ref.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, []);

  const save = async () => {
    if (saving || saveInFlightRef.current) return;
    if (!sealBarCode) {
      setSealError("Required");
      return;
    }

    if (addedSeals?.includes(sealBarCode)) {
      setAlreadyAddedDialogOpen(true);
      return;
    }

    saveInFlightRef.current = true;
    try {
      await saveSeal({ sealBarCode, notes, validated: true, isScanned });
      cancel();
    } catch (error) {
      logError("OutboundShipments:Seals:SealsCreate:save", error, notify);
    } finally {
      saveInFlightRef.current = false;
    }
  };

  const handleSubmitKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (saving || saveInFlightRef.current) return;
      save();
    }
  };

  const handleScanSuccess = (barcode) => {
    if (barcode !== ref.current.value) {
      beep(100, 520, 200);
    }
    setSealBarCode(barcode);
    setIsScannerOpen(false);
    setIsScanned(true);
  };

  const handleAlreadyAddedDialogClose = () => {
    setAlreadyAddedDialogOpen(false);
    setIsScanned(false);
    setSealBarCode("");
  };

  const handleNotInSealsNumberDialogClose = () => {
    setNotInSealsNumberDialogOpen(false);
    setIsScanned(false);
    setSealBarCode("");
  };

  const handleSaveAnyway = async () => {
    if (saving || saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    try {
      await saveSeal({
        sealBarCode,
        notes,
        validated: false,
        scanned: isScanned,
      });
      handleNotInSealsNumberDialogClose();
      cancel();
    } finally {
      saveInFlightRef.current = false;
    }
  };

  const onBarcodeInputChange = (e) => {
    if (e.target.value) {
      setSealError("");
    } else {
      setSealError("Required");
    }
    setSealBarCode(e.target.value);
    setIsScanned(false);
  };

  return (
    <>
      <Dialog open={true} fullWidth={true}>
        <Box
          component="span"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          m={2}
        >
          <Typography variant="h6">Create a Seal</Typography>
          <CancelOutlinedIcon fontSize="large" onClick={cancel} />
        </Box>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {isScannerOpen ? (
                <QrBarcodeScanner onScanSuccess={handleScanSuccess} />
              ) : (
                <Box display="flex" justifyContent="center">
                  <Button
                    disabled={saving}
                    onClick={() => setIsScannerOpen(true)}
                    color="primary"
                    variant="outlined"
                    margin="dense"
                  >
                    Rescan
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Seal Barcode"
                variant="outlined"
                fullWidth
                margin="dense"
                value={sealBarCode}
                onChange={onBarcodeInputChange}
                error={!!sealError}
                helperText={sealError}
                disabled={isScanned}
                inputRef={ref}
                onKeyDown={handleSubmitKeyDown}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Note"
                variant="outlined"
                fullWidth
                margin="dense"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} color="primary" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={alreadyAddedDialogOpen}
        onClose={handleAlreadyAddedDialogClose}
      >
        <DialogContent>
          <Typography>
            This seal is already added in the inbound shipment. Do you want to
            scan again?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAlreadyAddedDialogClose} color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notInSealsNumberDialogOpen}
        onClose={handleNotInSealsNumberDialogClose}
      >
        <DialogContent>
          <Typography>
            This seal number does not match our records, do you want to save it
            anyways?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNotInSealsNumberDialogClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveAnyway} color="primary" disabled={saving}>
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
